<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import {
  clinicalAccessEventsApi,
  type ClinicalAccessChannelGroup,
  type ClinicalAccessFilters,
  type ClinicalAccessPerson,
} from '@/api/clinical-access-events';
import { useFileDownload } from '@/composables/useFileDownload';
import { useMobile } from '@/composables/useMobile';
import { requestErrorMessage } from '@/utils/api-errors';
import SectionHeader from '@/components/section-header.vue';

// The patient's access log (DS 41 art. 9): every Acceso to their clinical data,
// newest first, a page at a time, with the same filters as its CSV.
const props = defineProps<{ patientId: number }>();

// The server takes at most 366 days, both ends included.
const MAX_RANGE_DAYS = 366;
const DEFAULT_RANGE_DAYS = 30;
const MS_PER_DAY = 86_400_000;
const DATE_PART_LENGTH = 2;
// The server caps a page at 100 Accesos.
const PER_PAGE = 25;
const MEDIUM_PER_PAGE = 50;
const MAX_PER_PAGE = 100;
const PER_PAGE_OPTIONS = [PER_PAGE, MEDIUM_PER_PAGE, MAX_PER_PAGE];

const { isMobile } = useMobile();
const { downloadFile } = useFileDownload();

function isoDate(date: Date): string {
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map(part => String(part).padStart(DATE_PART_LENGTH, '0')).join('-');
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / MS_PER_DAY);
}

const today = new Date();
const from = ref(isoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - DEFAULT_RANGE_DAYS + 1)));
const to = ref(isoDate(today));
// The whole option, so the field keeps its name when a new date range leaves the
// person out of the options.
const person = ref<ClinicalAccessPerson | null>(null);
const channel = ref<ClinicalAccessChannelGroup | null>(null);
const page = ref(1);
const perPage = ref(PER_PAGE);

// Mirrors ClinicalAccessEvent::CHANNEL_GROUPS and its labels.
const channelOptions: { title: string; value: ClinicalAccessChannelGroup }[] = [
  { title: 'Web', value: 'web' },
  { title: 'App profesional', value: 'caregiver_app' },
  { title: 'App Familia', value: 'family_app' },
  { title: 'Kuido', value: 'kuido' },
  { title: 'Correo', value: 'email' },
  { title: 'Exportación', value: 'export' },
];

// One message for both dates, read out by each of them.
const rangeErrorId = useId();
const rangeError = computed(() => {
  if (!from.value || !to.value) return 'Selecciona ambas fechas.';

  const days = daysBetween(from.value, to.value);
  if (days < 0) return 'La fecha inicial debe ser menor o igual a la fecha final.';
  if (days + 1 > MAX_RANGE_DAYS) return `El rango no puede superar los ${MAX_RANGE_DAYS} días.`;

  return '';
});

const filters = computed<ClinicalAccessFilters>(() => ({
  from: from.value,
  to: to.value,
  person: person.value?.value ?? null,
  channel: channel.value,
}));

watch(filters, () => {
  page.value = 1;
});

const { data, isFetching: loading, error } = useQuery({
  queryKey: ['clinicalAccessEvents', computed(() => props.patientId), filters, page, perPage],
  queryFn: () => clinicalAccessEventsApi.index(props.patientId, filters.value, page.value, perPage.value),
  enabled: computed(() => !rangeError.value),
  placeholderData: keepPreviousData,
});
const errorText = computed(() => (error.value ?
  requestErrorMessage(error.value, 'No se pudo cargar el registro de accesos. Intenta nuevamente.') :
  ''));

const headers = [
  { title: 'Fecha y hora', key: 'firstAt', sortable: false, nowrap: true },
  { title: 'Persona', key: 'person', sortable: false },
  { title: 'Canal', key: 'humanChannel', sortable: false },
  { title: 'Acción', key: 'humanAction', sortable: false },
  { title: 'Sección', key: 'humanSection', sortable: false },
];

const downloading = ref(false);
const downloadError = ref('');

async function download() {
  downloading.value = true;
  downloadError.value = '';
  try {
    downloadFile(await clinicalAccessEventsApi.download(props.patientId, filters.value));
  } catch (downloadFailure) {
    downloadError.value = requestErrorMessage(downloadFailure, 'No se pudo descargar el CSV. Intenta nuevamente.');
  } finally {
    downloading.value = false;
  }
}
</script>

<template>
  <div>
    <section-header
      icon="mdi-shield-account"
      title="Accesos a la ficha clínica"
      class="mb-2"
    />
    <p class="text-body-2 text-medium-emphasis mb-4">
      Quién vio, imprimió, exportó o recibió por correo datos clínicos de este paciente.
      Las vistas iguales de una misma persona dentro de 10 minutos se cuentan juntas.
    </p>

    <v-alert
      v-if="errorText || downloadError"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-3"
    >
      {{ errorText || downloadError }}
    </v-alert>

    <v-row
      dense
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="6"
        md="2"
      >
        <v-text-field
          v-model="from"
          data-testid="access-log-from"
          type="date"
          label="Desde"
          variant="outlined"
          density="compact"
          :error="!!rangeError"
          :aria-invalid="rangeError ? 'true' : undefined"
          :aria-describedby="rangeError ? rangeErrorId : undefined"
          hide-details
        />
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="2"
      >
        <v-text-field
          v-model="to"
          data-testid="access-log-to"
          type="date"
          label="Hasta"
          variant="outlined"
          density="compact"
          :error="!!rangeError"
          :aria-invalid="rangeError ? 'true' : undefined"
          :aria-describedby="rangeError ? rangeErrorId : undefined"
          hide-details
        />
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="3"
      >
        <v-autocomplete
          v-model="person"
          data-testid="access-log-person"
          :items="data?.people ?? []"
          return-object
          label="Persona"
          variant="outlined"
          density="compact"
          no-data-text="No hay personas en este rango de fechas"
          clearable
          hide-details
        />
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="2"
      >
        <v-select
          v-model="channel"
          data-testid="access-log-channel"
          :items="channelOptions"
          label="Canal"
          variant="outlined"
          density="compact"
          clearable
          hide-details
        />
      </v-col>
      <v-col
        cols="12"
        md="3"
        class="d-flex justify-md-end"
      >
        <v-btn
          data-testid="access-log-download"
          prepend-icon="mdi-file-delimited"
          variant="tonal"
          color="primary"
          height="40"
          :block="isMobile"
          :loading="downloading"
          :disabled="!!rangeError"
          @click="download"
        >
          Descargar CSV
        </v-btn>
      </v-col>
    </v-row>
    <div
      v-if="rangeError"
      :id="rangeErrorId"
      data-testid="access-log-range-error"
      class="text-caption text-error mb-2"
      role="alert"
    >
      {{ rangeError }}
    </div>

    <v-data-table-server
      v-model:page="page"
      v-model:items-per-page="perPage"
      :headers="headers"
      :items="data?.events ?? []"
      :items-length="data?.totalCount ?? 0"
      :items-per-page-options="PER_PAGE_OPTIONS"
      :loading="loading"
      :mobile="isMobile"
      item-value="id"
      density="compact"
      no-data-text="No hay accesos para los filtros seleccionados."
      items-per-page-text="Accesos por página"
    >
      <template #[`item.firstAt`]="{ item }">
        <div>{{ item.humanFirstAt }}</div>
        <div
          v-if="item.viewsCount > 1"
          class="text-caption text-medium-emphasis"
        >
          {{ item.viewsCount }} vistas · hasta {{ item.humanLastAt }}
        </div>
      </template>
    </v-data-table-server>
  </div>
</template>
